import { isBefore, isWithinInterval } from 'date-fns';
import { useCallback } from 'react';
import styled from 'styled-components';

import { Text, colors } from '@soluto-private/mx-asurion-ui-react';

import { useIsMobile } from '@service-initiation/shared-ui-breakpoints';
import { useSelectedStoreAsyncAtom } from '@service-initiation/tech-care-data-access-store';
import {
  convertTime24to12,
  daysOfWeek,
} from '@service-initiation/tech-care-utils-date-time';

import { ReactComponent as LocationSvg } from '../assets/location.svg';
import { isSlotType, nextDayStoreIsOpenIndex } from '../utils';

const DEFAULT_TIME = ['0', '0', '0'];

const LocationSvgStyled = styled(LocationSvg)`
  width: 20px;
  height: 20px;

  && {
    path {
      fill: ${colors.black};
    }
  }
`;

export const StoreNameHeader = () => {
  const isMobile = useIsMobile();
  const { value: selectedStore } = useSelectedStoreAsyncAtom();
  const nowTime = new Date();

  const headerTextSize = isMobile ? 2 : 3;
  const todayIndex = nowTime.getDay();

  const todayStoreHours = selectedStore?.hours?.all[todayIndex];
  const storeOpen = isSlotType(todayStoreHours)
    ? todayStoreHours.open.split(':')
    : DEFAULT_TIME;
  const storeClose = isSlotType(todayStoreHours)
    ? todayStoreHours.close.split(':')
    : DEFAULT_TIME;

  const openTime = new Date(
    nowTime.getFullYear(),
    nowTime.getMonth(),
    nowTime.getDate(),
    Number(storeOpen[0]),
    Number(storeOpen[1]),
    Number(storeOpen[2])
  );
  const closeTime = new Date(
    nowTime.getFullYear(),
    nowTime.getMonth(),
    nowTime.getDate(),
    Number(storeClose[0]),
    Number(storeClose[1]),
    Number(storeClose[2])
  );
  const isOpenNow = isWithinInterval(nowTime, {
    end: closeTime,
    start: openTime,
  });

  const priorToOpen = isBefore(nowTime, openTime);

  const nextDayOpenIndex = nextDayStoreIsOpenIndex(
    selectedStore?.hours?.all || [],
    todayIndex
  );
  const nextDayOpenStoreHours = selectedStore?.hours?.all[nextDayOpenIndex];

  const getStoreOpenCloseText = useCallback(() => {
    if (!isSlotType(todayStoreHours)) {
      return '';
    }

    if (priorToOpen) {
      return `: Closed now. Opens at ${convertTime24to12(
        todayStoreHours.open
      )} today.`;
    }

    if (isOpenNow) {
      return `: Open now. Closes at ${convertTime24to12(
        todayStoreHours.close
      )}`;
    } else if (
      todayIndex + 1 === nextDayOpenIndex ||
      (nextDayOpenIndex === 0 &&
        selectedStore?.hours?.all?.length &&
        todayIndex === selectedStore?.hours?.all?.length - 1)
    ) {
      return isSlotType(nextDayOpenStoreHours)
        ? `: Closed now. Opens at ${convertTime24to12(
            nextDayOpenStoreHours.open
          )} tomorrow.`
        : '';
    } else if (nextDayOpenIndex <= 0) {
      return isSlotType(nextDayOpenStoreHours)
        ? `: Closed now. Opens at ${convertTime24to12(
            nextDayOpenStoreHours.open
          )} on ${daysOfWeek[nextDayOpenIndex]}.`
        : '';
    }
  }, [
    isOpenNow,
    selectedStore?.hours?.all?.length,
    nextDayOpenIndex,
    nextDayOpenStoreHours,
    priorToOpen,
    todayIndex,
    todayStoreHours,
  ]);

  return (
    <>
      <LocationSvgStyled aria-label="location" role="img" />
      <Text
        color={colors.black}
        data-testid="appointment-time-subtitle"
        size={headerTextSize}
        weight="base"
      >
        {selectedStore?.name}
        <Text
          color={colors.neutralDeepest}
          size={headerTextSize}
          weight="feather"
        >
          {getStoreOpenCloseText()}
        </Text>
      </Text>
    </>
  );
};
